import { COMMON_STATUS_LABEL, ACTION_LABEL } from "@/lib/i18nLabels";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FileClock, Search, Download, Filter as FilterIcon } from "lucide-react";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { fmtDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import StickyContextBar from "@/components/StickyContextBar";

/**
 * DecisionLogPage — 149-bv + 149-cq PROMPT 16-R2A.
 * Flatten view semua keputusan dari Critical Issues, dengan:
 *   • filter: tanggal (from/to), issue (code/title), decider, project, division, status issue, severity, source
 *   • search bar (client-side)
 *   • Export CSV mengikuti filter + scope aktif
 *   • append-only + histori versi tetap terjaga (backend enforcement)
 *   • hanya menampilkan keputusan dari issue yg berada dalam scope visibility user.
 */
const STATUS_OPTS = [
  { v: "open", label: COMMON_STATUS_LABEL.open }, { v: "in_review", label: COMMON_STATUS_LABEL.in_review },
  { v: "decision_required", label: COMMON_STATUS_LABEL.decision_required }, { v: "in_progress", label: COMMON_STATUS_LABEL.in_progress },
  { v: "resolved", label: "Resolved" }, { v: "closed", label: "Closed" },
];
const SEV_OPTS = [
  { v: "low", label: "Low" }, { v: "medium", label: "Medium" },
  { v: "high", label: "High" }, { v: "critical", label: "Critical" },
];
const SOURCE_OPTS = [
  { v: "manual", label: "Manual" }, { v: "data_integrity", label: "Data Integrity" },
];

export default function DecisionLogPage() {
  const [rows, setRows] = useState([]);
  const [issues, setIssues] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({
    date_from: "", date_to: "",
    issue_id: "all", project_id: "all", division: "all",
    issue_status: "all", severity: "all", source: "all",
    decider_id: "all",
  });
  const [exporting, setExporting] = useState(false);

  const buildParams = useCallback(() => {
    const p = new URLSearchParams();
    if (filters.date_from) p.set("date_from", filters.date_from);
    if (filters.date_to) p.set("date_to", filters.date_to);
    if (filters.issue_id !== "all") p.set("issue_id", filters.issue_id);
    if (filters.project_id !== "all") p.set("project_id", filters.project_id);
    if (filters.division !== "all") p.set("division", filters.division);
    if (filters.source !== "all") p.set("source", filters.source);
    if (filters.decider_id !== "all") p.set("decider_id", filters.decider_id);
    return p;
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams();
      const [decRes, issuesRes, projRes] = await Promise.all([
        api.get(`/critical-issues/decisions/filtered${params.toString() ? `?${params}` : ""}`),
        api.get("/critical-issues/scoped"),
        api.get("/active-projects").catch(() => ({ data: [] })),
      ]);
      setRows(decRes.data || []);
      setIssues(issuesRes.data || []);
      setProjects(projRes.data || []);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal memuat decision log"); }
    finally { setLoading(false); }
  }, [buildParams]);
  useEffect(() => { load(); }, [load]);

  // Client-side status + severity + search karena backend belum expose semua field
  // (rows sudah membawa issue_status/severity dari _decision_rows).
  const filtered = useMemo(() => {
    let out = rows;
    if (filters.issue_status !== "all") out = out.filter((r) => r.issue_status === filters.issue_status);
    if (filters.severity !== "all") out = out.filter((r) => r.issue_severity === filters.severity);
    if (q.trim()) {
      const needle = q.toLowerCase();
      out = out.filter((r) =>
        (r.decision || "").toLowerCase().includes(needle)
        || (r.issue_code || "").toLowerCase().includes(needle)
        || (r.issue_title || "").toLowerCase().includes(needle)
        || (r.decided_by_name || "").toLowerCase().includes(needle));
    }
    return out;
  }, [rows, q, filters.issue_status, filters.severity]);

  const distinctDeciders = useMemo(() => {
    const map = new Map();
    for (const r of rows) if (r.decided_by && r.decided_by_name) map.set(r.decided_by, r.decided_by_name);
    return Array.from(map, ([v, label]) => ({ v, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);
  const distinctDivisions = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => r.division && s.add(r.division));
    return Array.from(s).sort();
  }, [rows]);

  const exportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const params = buildParams();
      const res = await api.get(`/critical-issues/decisions/export${params.toString() ? `?${params}` : ""}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const a = document.createElement("a");
      const url = URL.createObjectURL(blob);
      a.href = url;
      a.download = `decision_log_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export siap");
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal export"); }
    finally { setExporting(false); }
  };

  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const clearFilters = () => setFilters({
    date_from: "", date_to: "", issue_id: "all", project_id: "all",
    division: "all", issue_status: "all", severity: "all", source: "all", decider_id: "all",
  });

  return (
    <div className="p-6 space-y-4" data-testid="decision-log-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="overline text-slate-500">Kontrol & Audit</p>
          <h1 className="font-display text-3xl text-[#0B1D3A]">Log Keputusan</h1>
          <p className="text-xs text-slate-500 mt-1">Histori keputusan lintas semua Isu Kritis (append-only). Untuk revisi, buka isu asal.</p>
        </div>
        <Button data-testid="btn-export-dl" variant="outline" onClick={exportCsv} disabled={exporting}
          className="rounded-none uppercase text-xs tracking-wider font-semibold border-slate-400 text-slate-700 hover:bg-slate-100">
          <Download size={13} className="mr-1" /> {exporting ? "Menyiapkan…" : ACTION_LABEL.export}
        </Button>
      </div>

      <StickyContextBar
        segments={["Kontrol & Audit", "Log Keputusan", `${filtered.length} keputusan`]}
      />

      {/* Filter bar */}
      <div className="border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="dl-filters">
        <div className="flex items-center gap-2 flex-wrap">
          <FilterIcon size={13} className="text-slate-500" />
          <span className="overline text-slate-600 text-[10px]">Filter</span>
          <input type="date" data-testid="dl-date-from" value={filters.date_from} onChange={(e) => setFilter("date_from", e.target.value)}
            className="border border-slate-300 px-2 py-1 text-xs" placeholder="Dari" />
          <span className="text-[10px] text-slate-500">→</span>
          <input type="date" data-testid="dl-date-to" value={filters.date_to} onChange={(e) => setFilter("date_to", e.target.value)}
            className="border border-slate-300 px-2 py-1 text-xs" />
          <FilterSelect testid="dl-issue" value={filters.issue_id} onChange={(v) => setFilter("issue_id", v)}
            placeholder="Isu" options={issues.map((i) => ({ v: i.id, label: `${i.code} — ${(i.title || "").slice(0, 40)}` }))} />
          <FilterSelect testid="dl-project" value={filters.project_id} onChange={(v) => setFilter("project_id", v)}
            placeholder="Proyek" options={useMemo(() => {
              // 149-cr (PROMPT 16-R2A-R1): dropdown project HANYA main project.
              // Dedup by id, sort by name. VO/WO tidak diulang di sini —
              // keputusan pada VO/WO tetap ter-fetch by filter parent project.
              // Kalau nama project sama tapi ID beda (kasus test data / project
              // dengan nama identik), tempel suffix ID pendek supaya user bisa
              // membedakan.
              const mains = (projects || []).filter((p) => p.project_type === "new_project" && !p.parent_project_id);
              const seen = new Set();
              const rows = [];
              for (const p of mains) {
                if (seen.has(p.id)) continue;
                seen.add(p.id);
                rows.push({ id: p.id, name: p.project_name || p.id });
              }
              const nameCount = rows.reduce((acc, r) => { acc[r.name] = (acc[r.name] || 0) + 1; return acc; }, {});
              const out = rows.map((r) => ({
                v: r.id,
                label: nameCount[r.name] > 1
                  ? `${r.name.slice(0, 45)} · #${r.id.slice(0, 6)}`
                  : r.name.slice(0, 60),
              }));
              out.sort((a, b) => a.label.localeCompare(b.label));
              return out;
            }, [projects])} />
          <FilterSelect testid="dl-division" value={filters.division} onChange={(v) => setFilter("division", v)}
            placeholder="Divisi" options={distinctDivisions.map((d) => ({ v: d, label: d }))} />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <FilterSelect testid="dl-status" value={filters.issue_status} onChange={(v) => setFilter("issue_status", v)}
            placeholder="Status Isu" options={STATUS_OPTS} />
          <FilterSelect testid="dl-severity" value={filters.severity} onChange={(v) => setFilter("severity", v)}
            placeholder="Tingkat Keparahan" options={SEV_OPTS} />
          <FilterSelect testid="dl-source" value={filters.source} onChange={(v) => setFilter("source", v)}
            placeholder="Sumber" options={SOURCE_OPTS} />
          <FilterSelect testid="dl-decider" value={filters.decider_id} onChange={(v) => setFilter("decider_id", v)}
            placeholder="Pengambil Keputusan" options={distinctDeciders} />
          <div className="relative flex-1 min-w-[220px]">
            <Search size={13} className="absolute left-2.5 top-2 text-slate-400" />
            <input data-testid="dl-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Cari keputusan / kode isu / pengambil keputusan…"
              className="pl-8 pr-3 py-1.5 border border-slate-300 text-xs w-full focus:outline-none focus:border-[#FF5A1F]" />
          </div>
          <button type="button" data-testid="dl-clear" onClick={clearFilters}
            className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 hover:text-rose-700">{ACTION_LABEL.reset}</button>
        </div>
      </div>

      <section className="bg-white border border-slate-200" data-testid="dl-list">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200 ens-sticky-thead">
            <tr className="text-left">
              <th className="px-3 py-2">Tanggal</th>
              <th className="px-3 py-2">Isu</th>
              <th className="px-3 py-2">v</th>
              <th className="px-3 py-2">Keputusan</th>
              <th className="px-3 py-2">Pengambil Keputusan</th>
              <th className="px-3 py-2">Tindak Lanjut</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="p-6 text-center text-slate-400 italic">Memuat…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-slate-400 italic" data-testid="dl-empty">Tidak ada keputusan yang cocok dengan filter.</td></tr>
            )}
            {filtered.map((r, i) => (
              <tr key={`${r.issue_id}-${r.version}-${i}`} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`dl-row-${r.issue_id}-${r.version}`}>
                <td className="px-3 py-2 tabular-nums text-slate-700 whitespace-nowrap">{fmtDate(r.decided_at)}</td>
                <td className="px-3 py-2">
                  <a href={`/admin/critical-issues?id=${r.issue_id}`} className="font-mono text-sky-700 hover:underline">{r.issue_code}</a>
                  <div className="text-[10px] text-slate-500 truncate max-w-[200px]">{r.issue_title}</div>
                </td>
                <td className="px-3 py-2 font-mono text-slate-500">v{r.version}</td>
                <td className="px-3 py-2 text-slate-800 max-w-[320px]">
                  <div className="line-clamp-2">{r.decision}</div>
                  {r.revision_reason && <div className="text-[9px] text-amber-700 uppercase tracking-wider mt-0.5">↻ Revisi: {r.revision_reason}</div>}
                </td>
                <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{r.decided_by_name}</td>
                <td className="px-3 py-2 text-slate-600">
                  {(r.follow_ups || []).length > 0 ? (
                    <span className="text-[10px] font-semibold text-orange-600">{r.follow_ups.length} aksi</span>
                  ) : (
                    <span className="text-slate-400 italic">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function FilterSelect({ testid, value, onChange, placeholder, options }) {
  return (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
      className="h-8 border border-slate-300 px-2 text-xs bg-white focus:outline-none focus:border-[#FF5A1F] max-w-[220px]">
      <option value="all">{`Semua ${placeholder}`}</option>
      {options.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
    </select>
  );
}
